// Forecast Accuracy Widget — pairs the previous week's archived
// planning snapshot (planned hours per worker / per day) with the
// actual picking activity for the same ISO week (from picking_tasks).
//
// Surfaces three high-value Lean Six Sigma signals:
//   1. Overall planned vs actual deviation (% over/under).
//   2. Per-day deviation bars (which day was the plan furthest from reality).
//   3. Top under-staffed days (actual > planned) and over-staffed days
//      (planned > actual), plus a planned-but-no-show counter so the user
//      can chase down absentees fast.
//
// The widget lives on the main Dashboard. It auto-targets the previous
// completed ISO week so users see "how did we do last week?" without
// having to pick a date.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Activity, AlertTriangle, ArrowRight } from 'lucide-react';
import { BRAND, fmtMins, previousIsoWeek } from '.';
import { WeekPicker } from '../../planning/WeekPicker';
import { safeFetchJson } from '@/utils/safeFetch';

const API = process.env.REACT_APP_BACKEND_URL;

const DEV_GREEN = '#10b981';
const DEV_AMBER = '#f59e0b';
const DEV_RED = '#ef4444';

const devColor = (pct) => {
  if (pct == null) return '#94a3b8';
  const a = Math.abs(pct);
  if (a <= 5) return DEV_GREEN;
  if (a <= 15) return DEV_AMBER;
  return DEV_RED;
};

export const ForecastAccuracyWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Persist last-viewed week so users don't lose context across reloads.
  const [weekId, setWeekId] = useState(() => {
    try { return localStorage.getItem('forecast.weekId') || previousIsoWeek(); }
    catch { return previousIsoWeek(); }
  });
  useEffect(() => { try { localStorage.setItem('forecast.weekId', weekId); } catch { /* ignore */ } }, [weekId]);
  // Default to presence-corrected actuals — that's where the user keys
  // in sick / no-show / overtime, so it's the source they expect to see
  // their work reflected in. Users with no overrides yet see actual =
  // planned (deviation 0) which is the friendly default. Users on
  // legacy SAP-driven workflows can flip to "Picking" once.
  const [source, setSource] = useState(() => localStorage.getItem('forecast.source') || 'presence');

  useEffect(() => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    if (!token) { setLoading(false); return; }
    setLoading(true);
    safeFetchJson(`${API}/api/planning-v2/dash/forecast-accuracy/${weekId}?source=${source}`, { token })
      .then(d => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [weekId, source]);

  const switchSource = (v) => {
    setSource(v);
    try { localStorage.setItem('forecast.source', v); } catch { /* ignore */ }
  };

  if (loading) {
    return (
      <Card data-testid="forecast-accuracy-widget">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="h-5 w-5" style={{ color: BRAND.cyan }} />
            Forecast Accuracy
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.planned_total_min === 0) {
    return (
      <Card data-testid="forecast-accuracy-widget">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="h-5 w-5" style={{ color: BRAND.cyan }} />
              Forecast Accuracy
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <WeekPicker value={weekId} onChange={setWeekId} />
              <SourceToggle value={source} onChange={switchSource} />
            </div>
          </div>
          <CardDescription className="text-xs">
            {weekId} has no planning to compare against.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-32 flex flex-col items-center justify-center text-center text-muted-foreground">
            <Activity className="h-7 w-7 opacity-40 mb-2" />
            <p className="text-sm">No archived plan for {weekId} yet.</p>
            <Link to={`/planning?week=${weekId}`} className="text-xs text-primary hover:underline mt-1">Open Planning →</Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  const overall = data.deviation_pct;
  const overallSign = overall == null ? null : overall >= 0 ? 'over' : 'under';
  const overallIcon = overallSign === 'over' ? <TrendingUp className="h-4 w-4" /> : overallSign === 'under' ? <TrendingDown className="h-4 w-4" /> : null;
  const overallColor = devColor(overall);

  // Per-day deviation bar set
  const maxAbs = Math.max(
    1,
    ...data.per_day.map(d => Math.abs(d.actual_min - d.planned_min)),
  );

  return (
    <Card data-testid="forecast-accuracy-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="h-5 w-5" style={{ color: BRAND.cyan }} />
            Forecast Accuracy
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <WeekPicker value={weekId} onChange={setWeekId} />
            <SourceToggle value={source} onChange={switchSource} />
            <Link to={`/planning?week=${weekId}&tab=accuracy&source=${source}`} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5" data-testid="forecast-accuracy-open">
              Open <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
        <CardDescription className="text-xs">
          Plan vs {source === 'presence' ? 'presence-corrected' : 'SAP picking'} actuals · {weekId} ({data.monday} → {source === 'presence' ? data.sunday : data.saturday})
          {!data.snapshot_used && <span className="ml-2 text-amber-600">· live schedule (no archive)</span>}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0 space-y-4">
        {/* KPI row */}
        <div className="grid grid-cols-3 gap-3" data-testid="forecast-kpi-row">
          <div className="p-3 rounded-lg border bg-muted/20">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Planned</p>
            <p className="text-xl font-bold tabular-nums">{fmtMins(data.planned_total_min)}</p>
          </div>
          <div className="p-3 rounded-lg border bg-muted/20">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Actual</p>
            <p className="text-xl font-bold tabular-nums">{fmtMins(data.actual_total_min)}</p>
          </div>
          <div className="p-3 rounded-lg border" style={{ background: `${overallColor}11`, borderColor: `${overallColor}55` }}>
            <p className="text-[11px] uppercase tracking-wide" style={{ color: overallColor }}>Deviation</p>
            <p className="text-xl font-bold tabular-nums flex items-center gap-1" style={{ color: overallColor }}>
              {overallIcon}
              {overall == null ? '—' : `${overall > 0 ? '+' : ''}${overall.toFixed(1)}%`}
            </p>
          </div>
        </div>

        {/* Per-day deviation bars */}
        <div data-testid="forecast-per-day">
          <p className="text-xs font-medium text-muted-foreground mb-2">Per-day deviation (actual − planned)</p>
          <div className="space-y-1.5">
            {data.per_day.map(d => {
              const deltaMin = d.actual_min - d.planned_min;
              const widthPct = (Math.abs(deltaMin) / maxAbs) * 50; // 50% half-track
              const color = devColor(d.deviation_pct);
              return (
                <div key={d.day_num} className="flex items-center gap-2 text-xs">
                  <span className="w-8 font-mono text-muted-foreground">{d.day}</span>
                  <div className="flex-1 h-5 bg-muted/40 rounded-md relative overflow-hidden">
                    <div className="absolute left-1/2 top-0 bottom-0 w-px bg-border" />
                    <div
                      className="absolute top-0 bottom-0"
                      style={{
                        background: color,
                        opacity: 0.85,
                        ...(deltaMin >= 0
                          ? { left: '50%', width: `${widthPct}%` }
                          : { right: '50%', width: `${widthPct}%` }),
                      }}
                    />
                  </div>
                  <span className="w-20 text-right font-mono tabular-nums" style={{ color }}>
                    {deltaMin === 0 ? '0:00' : (deltaMin > 0 ? '+' : '−') + fmtMins(Math.abs(deltaMin))}
                  </span>
                  <span className="w-12 text-right text-[11px] tabular-nums" style={{ color }}>
                    {d.deviation_pct == null ? '—' : `${d.deviation_pct > 0 ? '+' : ''}${d.deviation_pct}%`}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* No-show + match coverage / overtime */}
        <div className="flex items-center justify-between text-xs pt-2 border-t border-muted/50">
          <div className="flex items-center gap-2">
            <AlertTriangle className={`h-4 w-4 ${data.no_show_count > 0 ? 'text-rose-600' : 'text-muted-foreground'}`} />
            <span className={data.no_show_count > 0 ? 'font-semibold text-rose-700' : 'text-muted-foreground'} data-testid="forecast-no-show">
              {data.no_show_count} {source === 'presence' ? 'marked no-show' : 'planned-but-no-show'}
            </span>
            {source === 'presence' && data.sick_count > 0 && (
              <span className="text-amber-700 font-medium">· {data.sick_count} sick (80% paid)</span>
            )}
          </div>
          <span className="text-muted-foreground tabular-nums">
            {source === 'presence' && data.overtime_total_min > 0
              ? `+${fmtMins(data.overtime_total_min)} overtime`
              : `${data.matched_count}/${data.planned_employees_count} workers matched`}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

// Compact pill toggle for picking ↔ presence. Persists to localStorage
// so the user's choice survives navigation.
const SourceToggle = ({ value, onChange }) => (
  <div className="inline-flex rounded-full border bg-muted/30 p-0.5 text-[10px] uppercase tracking-wide" data-testid="forecast-source-toggle">
    {[
      { v: 'picking', l: 'Picking' },
      { v: 'presence', l: 'Presence' },
    ].map(o => (
      <button
        key={o.v}
        type="button"
        onClick={() => onChange(o.v)}
        className={`px-2.5 py-0.5 rounded-full transition-colors ${value === o.v ? 'bg-white shadow-sm font-semibold text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
        data-testid={`forecast-source-${o.v}`}
      >
        {o.l}
      </button>
    ))}
  </div>
);
